/* ============================================================
   YOLÈTE — Atelier : composez votre ceinture (tige + boucle)
   ------------------------------------------------------------
   Rendu "ceinture assemblée" : tige (cuir des books, coloris au
   choix) + boucle posée sur le passant. Les sangles détourées
   viennent de tiges-catalog.js (strapImg + cx/cy) ; les boucles
   des PNG transparents d'https://yolete.vercel.app/assets/img/cut/boucles/.
   Manifest boucles : https://cdn.shopify.com/s/files/1/0790/8714/1213/files/cut__atelier.js (BOUCLE_CUTOUTS).
   ============================================================ */

/* Largeur choisie (25 ou 40). Une tige 25 ne reçoit qu'une boucle 25, etc. */
let width = 40;

/* Tiges = coloris du site qui ont un détouré (tiges-catalog.js) : champ « Photo
   composeur » de la fiche Shopify, sinon détourés d'origine (composer-belts.js).
   Un cuir ajouté par Charlotte entre dans le composeur dès qu'elle y dépose sa
   ceinture détourée (21/10/2026). Filtrées par largeur ; boucle sur (cx, cy). */
function buildTiges(w) {
  const liste = PRODUCTS.filter(p => p.type === 'tige' && p.width === w && p.compo), cuirs = [];
  liste.forEach(p => { if (cuirs.indexOf(p.leather) === -1) cuirs.push(p.leather); });
  /* cuirs dans l'ordre du site, coloris dans l'ordre habituel (noir, marron, camel, havane) */
  return liste.sort((a, b) => (cuirs.indexOf(a.leather) - cuirs.indexOf(b.leather)) || (a.colorOrder - b.colorOrder))
    .map(p => ({ id: p.id.replace(/^tige-/, ''), name: p.familyName + ' ' + p.colorLabel, img: p.compo, aCaler: !!p.compoShopify,
                 photo: p.photo, dot: p.dot, cx: 0.5, cy: TIGE_CENTRE, price: p.price }));
}

/* Détouré déposé dans Shopify : Charlotte y met la ceinture détourée telle quelle. Le
   composeur la ramène au format de ses tiges, comme scripts/normalize_front_band.py l'a
   fait pour les tiges d'origine : canevas 1086 × 300, passant au milieu, face avant de
   110 px (40 mm) ou 62 px (25 mm) centrée à 0,555. La face se mesure sur la ligne des
   trous de poinçon ; sans trous lisibles (fourrure), la ceinture est calée par le bas.
   Un détouré déjà à ce format est pris tel quel. */
const TIGE_CANEVAS = [1086, 300], TIGE_CENTRE = 0.555, TIGE_FACE = { 40: 110, 25: 62 }, TIGE_BAS = { 40: 232, 25: 205 };
const tigesCalees = {};
function caleTige(url, w) {
  const cle = url + '|' + w;
  if (!tigesCalees[cle]) tigesCalees[cle] = new Promise(resolve => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => {
      if (im.naturalWidth === TIGE_CANEVAS[0] && im.naturalHeight === TIGE_CANEVAS[1]) { resolve(url); return; }
      try { resolve(recadreTige(im, w)); } catch (e) { resolve(url); }
    };
    im.onerror = () => resolve(url);
    im.src = url;
  });
  return tigesCalees[cle];
}
function mediane(t) { const s = t.slice().sort((a, b) => a - b), m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; }
/* Face avant d'une ceinture ramenée à 960 px de large : { face, centre } en px, ou null. */
function mesureFace(d, W, H, w) {
  const N = W * H, op = new Uint8Array(N);
  for (let k = 0; k < N; k++) op[k] = d[k * 4 + 3] > 128 ? 1 : 0;
  const bas = [];
  for (let x = Math.floor(W / 3); x < Math.floor(2 * W / 3); x++)
    for (let y = H - 1; y >= 0; y--) if (op[y * W + x]) { bas.push(y); break; }
  if (!bas.length) return null;
  const bot = mediane(bas);
  /* transparent relié au bord = extérieur ; le transparent enclos = les trous */
  const ext = new Uint8Array(N), pile = [];
  const sortie = k => { if (!op[k] && !ext[k]) { ext[k] = 1; pile.push(k); } };
  for (let x = 0; x < W; x++) { sortie(x); sortie((H - 1) * W + x); }
  for (let y = 0; y < H; y++) { sortie(y * W); sortie(y * W + W - 1); }
  while (pile.length) {
    const k = pile.pop(), x = k % W;
    if (x > 0) sortie(k - 1); if (x < W - 1) sortie(k + 1); if (k >= W) sortie(k - W); if (k < N - W) sortie(k + W);
  }
  const vu = new Uint8Array(N), trous = [];
  for (let k0 = 0; k0 < N; k0++) {
    if (op[k0] || ext[k0] || vu[k0]) continue;
    let n = 0, ymin = H, ymax = -1, xmin = W, xmax = -1;
    vu[k0] = 1; pile.push(k0);
    while (pile.length) {
      const k = pile.pop(), x = k % W, y = (k - x) / W;
      n++; if (y < ymin) ymin = y; if (y > ymax) ymax = y; if (x < xmin) xmin = x; if (x > xmax) xmax = x;
      for (const j of [x > 0 ? k - 1 : -1, x < W - 1 ? k + 1 : -1, k >= W ? k - W : -1, k < N - W ? k + W : -1])
        if (j >= 0 && !op[j] && !ext[j] && !vu[j]) { vu[j] = 1; pile.push(j); }
    }
    if (n >= 20 && n <= 2000 && xmin > W * 0.15 && xmax + 1 < W * 0.9) trous.push((ymin + ymax + 1) / 2);
  }
  const [lo, hi] = w === 40 ? [70, 135] : [40, 95];
  let ligne = [];
  for (const cy of trous) {
    const g = trous.filter(c => Math.abs(c - cy) <= 6), f = 2 * (bot - cy);
    if (f >= lo && f <= hi && g.length > ligne.length) ligne = g;
  }
  if (ligne.length < 3) return null;
  const centre = mediane(ligne);
  return { face: 2 * (bot - centre), centre };
}
function recadreTige(im, w) {
  /* 1. ceinture seule (alpha), ramenée à 960 px de large ; image réduite d'abord si énorme */
  const r = Math.min(1, 2400 / im.naturalWidth), W0 = Math.round(im.naturalWidth * r), H0 = Math.round(im.naturalHeight * r);
  const c0 = document.createElement('canvas'); c0.width = W0; c0.height = H0;
  const x0c = c0.getContext('2d'); x0c.drawImage(im, 0, 0, W0, H0);
  const a = x0c.getImageData(0, 0, W0, H0).data;
  let x0 = W0, x1 = -1, y0 = H0, y1 = -1;
  for (let y = 0; y < H0; y++) for (let x = 0; x < W0; x++)
    if (a[(y * W0 + x) * 4 + 3] > 8) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  if (x1 < 0) return im.src;
  const bw = x1 - x0 + 1, bh = y1 - y0 + 1, H1 = Math.max(1, Math.round(bh * 960 / bw));
  const c1 = document.createElement('canvas'); c1.width = 960; c1.height = H1;
  const x1c = c1.getContext('2d'); x1c.imageSmoothingQuality = 'high';
  x1c.drawImage(c0, x0, y0, bw, bh, 0, 0, 960, H1);
  /* 2. face avant à l'épaisseur des tiges d'origine, sans déborder du canevas */
  const m = mesureFace(x1c.getImageData(0, 0, 960, H1).data, 960, H1, w);
  const out = document.createElement('canvas'); out.width = TIGE_CANEVAS[0]; out.height = TIGE_CANEVAS[1];
  const ox = out.getContext('2d'); ox.imageSmoothingQuality = 'high';
  if (!m) ox.drawImage(c1, (TIGE_CANEVAS[0] - 960) / 2, Math.max(0, TIGE_BAS[w] - H1));
  else {
    const s = Math.min(TIGE_FACE[w] / m.face, (TIGE_CANEVAS[0] - 20) / 960), L = Math.round(960 * s);
    ox.drawImage(c1, Math.round((TIGE_CANEVAS[0] - L) / 2), Math.round(TIGE_CANEVAS[1] * TIGE_CENTRE - m.centre * s), L, Math.round(H1 * s));
  }
  return out.toDataURL('image/png');
}
let TIGES = buildTiges(width);

/* Échanges de place dans l'atelier, demandés le 19 septembre. */
const ECHANGES_BOUCLES = [['boucle-n01', 'boucle-n20']];

/* Composition d'ouverture : Preto noire (même épuisée, Nathan, 6 oct 2026) et la
   première boucle en stock de la collection Best-sellers, dans l'ordre du classement
   du mois et à la largeur choisie (28 oct 2026). Avant, la N20 ouvrait le composeur
   même épuisée et l'ajout au panier depuis le composeur avait chuté de moitié. Sans
   best-seller en stock, la N20 reste la boucle d'ouverture. Une adresse qui demande
   une autre pièce (lien depuis une fiche) passe avant. La boucle d'ouverture est
   aussi la première carte du rail. */
const DEFAUT = { tige: { 40: 'preto-black-40', 25: 'preto-black-25' }, boucle: { 40: 'boucle-n20', 25: 'boucle-n20-25' } };
function tigeDefaut() { return TIGES.find(t => t.id === DEFAUT.tige[width]) || TIGES[0]; }
function boucleDefaut() { return BOUCLES[0]; }
function boucleOuverture(liste, w) {
  const best = (window.SHOP && window.SHOP.bestsellers) || [];
  for (const h of best) {
    const b = liste.find(x => x.id === h && !boucleOut(x));
    if (b) return b;
  }
  return liste.find(b => b.id === DEFAUT.boucle[w]) || null;
}

/* Boucles = celles détourées proprement, avec leur PNG transparent + aspect (w/h) */
const CUT = Array.isArray(BOUCLE_CUTOUTS)
  ? Object.fromEntries(BOUCLE_CUTOUTS.map(h => [h, 1]))   // ancien format (liste)
  : BOUCLE_CUTOUTS;                                        // { handle: aspect }
/* Taille cible de la boucle (moyenne géométrique, en cqw) — même surface visuelle
   pour toutes. Une tige 25 mm est plus fine : la boucle doit l'être aussi, sinon
   elle paraît disproportionnée. */
const BUCKLE_SIZE_BY_WIDTH = { 40: 15.4, 25: 8.6 };  /* re-ancré face (x10.13/13.81) */
function buckleSize() { return BUCKLE_SIZE_BY_WIDTH[width] || 15.5; }

/* Doublons à masquer dans l'atelier */
const EXCLUDE = new Set(['boucle-n162-1', 'boucle-n20-gold']);
/* Réglages fins par boucle : scale (taille), dy (décalage vertical, + = plus bas) */
const BUCKLE_TWEAKS = {
  'boucle-n161':       { scale: 0.85 },   // N161 un peu plus petite
  'boucle-n162':       { scale: 1.15 },   // N162 un peu plus grande
  'boucle-n138-copie': { dy: 0.04 },      // N169 recentrée un peu plus bas
  'boucle-n222':       { dy: 0.035 },     // N222 recentrée un peu plus bas
  'boucle-n408-25':    { scale: 0.80 },   // N408 très allongée : rendue plus discrète
  /* Boucles vendues avec leur passant : le PNG contient les deux pièces, passant à gauche,
     boucle à droite. mm = boîte complète, dx recentre la boucle sur le passant de la tige. */
  /* Les quatre boucles 25 mm ci-dessous étaient dessinées à l'échelle du 40 mm :
     la feuille « Dimensions boucles » a recopié les cotes du 40 pour N354/25,
     N411/25 et N412/25, et N50/25 paraissait aussi trop grande (signalé par
     Nathan le 21 sept 2026). Cotes PROVISOIRES = cotes du 40 mm × 25/40, en
     attendant la mesure réelle par la maison. */
  'boucle-n354-25': { mm: [44, 34] },    // 40 mm : 70 × 55
  'boucle-n411-25': { mm: [50, 38] },    // 40 mm : 80 × 60
  'boucle-n412-25': { mm: [31, 47] },    // 40 mm : 50 × 75
  'boucle-n50-25':  { mm: [53, 41] },    // 40 mm : 85 × 65 (la feuille disait 65 × 50)
  'boucle-n335-1': { mm: [98, 76], dx: 0.038 },
  'boucle-n312':   { mm: [118, 76], dx: 0.048 },   // boucle 90 mm + passant 18 mm (fichier maison)
  'boucle-n379': { mm: [124, 78], dx: 0.059 },
  'boucle-n380': { mm: [89, 62], dx: 0.042 },
};

/* Photo composeur déposée dans Shopify (champ personnalisé de chaque variante,
   24/09/2026) : Charlotte ajoute une boucle au composeur sans passer par le code.
   La carte montre la finition qu'elle mettra au panier : la première finition en
   stock qui a sa photo, sinon la première qui en a une (carte « Épuisée »). Elle
   prime sur les détourages du site, ce qui permet aussi d'en corriger un. */
function photoComposeur(p) {
  let vs = (p.variants || []).filter(v => v && v.compo);
  /* Sans photo de variante, la photo de la fiche vaut pour la première finition :
     c'est le cas d'une boucle sans options, dont l'admin ne montre pas de page
     de variante (test de la N420, 24/09/2026). */
  if (!vs.length && p.compoProduit && (p.variants || []).length)
    vs = [Object.assign({}, p.variants[0], { compo: p.compoProduit, compoAr: p.compoProduitAr || 1 })];
  if (!vs.length) return null;
  const v = vs.find(x => x.available) || vs[0];
  return { img: v.compo, aspect: +v.compoAr > 0 ? +v.compoAr : 1, voption: v.title || v.option1 || '' };
}

/* Même set que la boutique : une boucle par design (dédoublonnage par code de
   base), en ne gardant que celles qui ont un détourage propre, du site ou de
   Shopify. On préfère la variante correspondant à la largeur choisie. */
const cutList = PRODUCTS.filter(p => p.type === 'boucle' && ((p.id in CUT) || photoComposeur(p)) && !EXCLUDE.has(p.id)
  && (typeof inFolderBoucle !== 'function' || inFolderBoucle(p)));

/* Boucles détourées d'une largeur donnée (une carte par design) */
function buildBoucles(w) {
  const best = new Map();
  for (const p of cutList) {
    if (p.width !== w) continue;                 // uniquement la largeur choisie
    const key = (typeof boucleBase === 'function') ? boucleBase(p) : p.id;
    const prev = best.get(key);
    if (!prev || p.name.length < prev.name.length) best.set(key, p);
  }
  /* Une carte par coloris réellement vendu, quand la découpe existe dans cette
     finition (table https://yolete.vercel.app/assets/img/cut/coloris.js). Sinon une seule carte, comme avant.
     uid identifie la carte, id reste la référence produit pour le prix et le panier. */
  const COL = (typeof window !== 'undefined' && window.BOUCLE_COLORIS) || {};
  /* Variante Shopify exacte derriere la carte. Une boucle peut avoir plusieurs
     modeles dans ses options (Chorus et Valentine sur la N417) : sans cette
     precision, le panier retombait sur la premiere variante en stock. */
  const VAR = (typeof window !== 'undefined' && window.BOUCLE_VARIANTE) || {};
  const out = [];
  for (const p of best.values()) {
    const ph = photoComposeur(p);
    if (ph) { out.push({ ...p, uid: p.id, voption: ph.voption, img: ph.img, aspect: ph.aspect, deShopify: true }); continue; }
    const cols = COL[p.id];
    if (cols && cols.length) {
      cols.forEach(c => out.push({ ...p,
        uid: p.id + (c.s || ''),
        name: cols.length > 1 ? p.name + ' · ' + c.l : p.name,
        colorLabel: c.c,
        voption: c.c || VAR[p.id + (c.s || '')] || '',
        img: (window.YOLETE_CUT && YOLETE_CUT[p.id + (c.s || '')]) || `https://cdn.shopify.com/s/files/1/0790/8714/1213/files/cut__boucles__${p.id}${c.s || ''}.png?v=1791376181`,
        aspect: c.a || CUT[p.id] || 1 }));
    } else {
      out.push({ ...p, uid: p.id, voption: VAR[p.id] || '',
        img: (window.YOLETE_CUT && YOLETE_CUT[p.id]) || `https://cdn.shopify.com/s/files/1/0790/8714/1213/files/cut__boucles__${p.id}.png?v=1791376181`, aspect: CUT[p.id] || 1 });
    }
  }
  /* L'atelier suit le stock de la boutique : une boucle épuisée sur Shopify
     n'a pas à être composable. Si le filtre vidait la largeur, on garde la
     liste entière plutôt que de présenter un atelier sans boucle. */
  /* Règle de Nathan (23/09/2026) : une boucle à stock 0 reste visible, marquée
     « Épuisée » et non sélectionnable ; une boucle en brouillon ou retirée de la
     vente disparaît. Les épuisées passent en fin de rail. */
  const enLigne = out.filter(b => !boucleHorsLigne(b));
  const retenues = enLigne.length ? enLigne : out;
  const liste = retenues.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  /* L'atelier est classé par référence, à quelques échanges près demandés par
     la maison. Chaque paire échange simplement ses deux places, le reste du
     classement ne bouge pas. La première carte est aussi celle qui s'affiche
     à l'ouverture du composeur. */
  ECHANGES_BOUCLES.forEach(paire => {
    const i = liste.findIndex(b => b.id === paire[0]);
    const j = liste.findIndex(b => b.id === paire[1]);
    if (i >= 0 && j >= 0 && !boucleOut(liste[i]) && !boucleOut(liste[j])) { const t = liste[i]; liste[i] = liste[j]; liste[j] = t; }
  });
  /* après les échanges, les épuisées passent en fin de rail, sauf la boucle
     d'ouverture, en tête */
  const ordre = liste.filter(b => !boucleOut(b)).concat(liste.filter(b => boucleOut(b)));
  const ouverture = boucleOuverture(ordre, w);
  return ouverture ? [ouverture].concat(ordre.filter(b => b !== ouverture)) : ordre;
}
let BOUCLES = buildBoucles(width);

/* ---------- État (initialisé depuis l'URL si présent) ---------- */
const q = new URLSearchParams(location.search);
/* Une fiche peut envoyer l'identifiant de sa tige avec le préfixe « tige- » : on l'accepte. */
const tigeDemandee = (q.get('tige') || '').replace(/^tige-/, '');
let sel = {
  tige:   TIGES.find(t => t.id === tigeDemandee)      || tigeDefaut(),
  boucle: BOUCLES.find(b => (b.uid || b.id) === q.get('boucle')) || BOUCLES.find(b => b.id === q.get('boucle')) || boucleDefaut(),
};
let boucleStyle = 'all';
/* Taille de la tige, demandée AVANT l'ajout au panier (18 sept 2026). Avant, la
   tige partait sans taille, restait « en attente » dans le tiroir et le bouton
   « Passer commande » restait grisé : 85 % des paniers mobiles avec une ceinture
   ne pouvaient pas être commandés. */
const TAILLES = (typeof TAILLES_TIGE !== 'undefined') ? TAILLES_TIGE : ['75', '80', '85', '90', '95', '100', '105', '110'];
sel.taille = TAILLES.includes(q.get('t')) ? q.get('t') : null;

/* ---------- Éléments ---------- */
const strapImg     = document.getElementById('beltStrap');
const buckleImg    = document.getElementById('beltBuckle');
const tigeRail     = document.getElementById('tigeRail');
const boucleRail   = document.getElementById('boucleRail');
const boucleStyles = document.getElementById('boucleStyles');
const tailleChips  = document.getElementById('tailleChips');
const tailleBlock  = document.getElementById('tailleBlock');
const composeBar   = document.getElementById('composeBar');

/* ---------- Vignettes ---------- */
function swatch(item, kind, active) {
  return `
    <button class="swatch${active ? ' active' : ''}" data-${kind}="${item.uid || item.id}">
      <span class="swatch-media on-paper"><img src="${item.img}" alt="${item.name}" loading="lazy" style="object-fit:contain" /></span>
      <span class="swatch-name">${item.name}</span>
      <span class="swatch-sub">${euro(item.price)}</span>
    </button>`;
}

function tigeOut(t) { try { return typeof yoleteTigeStock === 'function' && yoleteTigeStock(t.id) !== null && yoleteTigeStock(t.id) <= 0; } catch (e) { return false; } }
/* Une finition epuisee doit se voir sur la carte. Avant, elle partait quand meme
   au panier et Shopify livrait un autre modele. */
/* Brouillon ou retirée de la vente : absente du catalogue Shopify de la page.
   Sur la copie de travail, sans catalogue Shopify, rien n'est considéré hors ligne. */
function boucleHorsLigne(b) { try { const B = window.__yoleteCart && window.__yoleteCart.BYH; return !!(B && Object.keys(B).length && !B[b.id]); } catch (e) { return false; } }
function boucleOut(b) { try { return typeof yoleteBoucleOut === 'function' && yoleteBoucleOut(b.id, b.voption || ''); } catch (e) { return false; } }
/* Tige épuisée : comme une boucle épuisée, elle reste sélectionnable et se
   compose. C'est à l'ajout au panier que le site le signale (6 oct 2026). */
function renderTiges() {
  tigeRail.innerHTML = TIGES.map(t => swatch(t, 'tige', t.id === sel.tige.id).replace('class="swatch', tigeOut(t) ? 'style="opacity:.6" class="swatch swatch-epuisee' : 'class="swatch').replace(/<span class="swatch-sub">[^<]*<\/span>/, m => tigeOut(t) ? '<span class="swatch-sub" style="letter-spacing:.1em;text-transform:uppercase;font-size:10px">Épuisée</span>' : m)).join('');
  majDefilement(tigeRail, TIGES.length, 'tigeMore', 'cuirs');
}

function renderStyles() {
  const styles = [...new Set(BOUCLES.map(b => b.style).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
  /* Un seul choix possible : le bouton « Toutes » ne filtre rien, on ne l'affiche pas. */
  if (!styles.length) { boucleStyles.innerHTML = ''; boucleStyles.hidden = true; return; }
  boucleStyles.hidden = false;
  boucleStyles.innerHTML =
    `<button class="filter${boucleStyle === 'all' ? ' active' : ''}" data-bstyle="all">Toutes</button>` +
    styles.map(s => `<button class="filter${boucleStyle === s ? ' active' : ''}" data-bstyle="${s}">${s}</button>`).join('');
}

function renderTaille() {
  if (!tailleChips) return;
  tailleChips.innerHTML = TAILLES.map(t =>
    `<button class="pd-chip${sel.taille === t ? ' active' : ''}" data-size="${t}" type="button">${t}</button>`).join('');
}

function renderBoucles() {
  const list = boucleStyle === 'all' ? BOUCLES : BOUCLES.filter(b => b.style === boucleStyle);
  boucleRail.innerHTML = list.map(b => swatch(b, 'boucle', (b.uid || b.id) === (sel.boucle.uid || sel.boucle.id)).replace('class="swatch', boucleOut(b) ? 'style="opacity:.6" class="swatch swatch-epuisee' : 'class="swatch').replace(/<span class="swatch-sub">[^<]*<\/span>/, m => boucleOut(b) ? '<span class="swatch-sub" style="letter-spacing:.1em;text-transform:uppercase;font-size:10px">Épuisée</span>' : m)).join('');
  majDefilement(boucleRail, list.length, 'boucleMore', 'boucles');
}

/* Les deux rails défilent dans leur propre zone, sur deux rangées exactement.
   La hauteur se cale sur la carte réelle, plus un aperçu de la rangée suivante
   pour qu'on voie tout de suite qu'il y a une suite. Téléphone, tablette et
   ordinateur suivent la même règle, seul le nombre de colonnes change. */
const RAILS = [];
function majDefilement(rail, n, idHint, mot) {
  const wrap = rail.parentElement, hint = document.getElementById(idHint);
  if (!wrap || !wrap.classList.contains('rail-wrap')) return;
  if (!RAILS.some(r => r.rail === rail)) RAILS.push({ rail: rail, n: n, idHint: idHint, mot: mot });
  else RAILS.forEach(r => { if (r.rail === rail) { r.n = n; r.mot = mot; } });
  requestAnimationFrame(() => {
    /* Téléphone (21 sept 2026) : une seule rangée qui défile vers la droite,
       la carte choisie amenée en vue. La hauteur n'est plus calculée. */
    if (window.matchMedia('(max-width: 720px)').matches) {
      rail.style.maxHeight = '';
      const defilable = rail.scrollWidth > rail.clientWidth + 4;
      wrap.classList.toggle('is-scrollable', defilable);
      wrap.classList.toggle('at-end', !defilable);
      if (hint) { hint.hidden = !defilable; hint.textContent = n + ' ' + mot + ' · faites défiler vers la droite'; }
      const on = rail.querySelector('.swatch.active');
      if (on && (on.offsetLeft < rail.scrollLeft || on.offsetLeft + on.offsetWidth > rail.scrollLeft + rail.clientWidth)) {
        try { rail.scrollTo({ left: Math.max(0, on.offsetLeft - 8), behavior: 'smooth' }); } catch (e) { rail.scrollLeft = Math.max(0, on.offsetLeft - 8); }
      }
      return;
    }
    const carte = rail.querySelector('.swatch');
    if (carte) {
      const h = carte.getBoundingClientRect().height;
      const st = getComputedStyle(rail);
      const gap = parseFloat(st.rowGap || st.gap) || 0;
      rail.style.maxHeight = Math.round(h * 2 + gap + Math.min(40, h * 0.3)) + 'px';
    }
    const defilable = rail.scrollHeight > rail.clientHeight + 4;
    wrap.classList.toggle('is-scrollable', defilable);
    wrap.classList.toggle('at-end', !defilable);
    if (hint) {
      hint.hidden = !defilable;
      hint.textContent = n + ' ' + mot + ' · faites défiler pour ' + (mot === 'boucles' ? 'les voir toutes' : 'les voir tous');
    }
  });
}
/* rotation du téléphone ou redimensionnement : on recalcule les deux hauteurs */
let minuteurRail = null;
window.addEventListener('resize', () => {
  clearTimeout(minuteurRail);
  minuteurRail = setTimeout(() => RAILS.forEach(r => majDefilement(r.rail, r.n, r.idHint, r.mot)), 180);
}, { passive: true });
[boucleRail, tigeRail].forEach(rail => rail.addEventListener('scroll', () => {
  const wrap = rail.parentElement;
  const fin = window.matchMedia('(max-width: 720px)').matches
    ? rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 6
    : rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 6;
  if (wrap) wrap.classList.toggle('at-end', fin);
}, { passive: true }));
/* Repères de mise en page, mesurés dans le document plutôt que devinés en CSS.
   --hdr      : hauteur de l'en-tête, pour épingler l'aperçu dessous sur téléphone.
   --bar      : hauteur de la barre d'ajout au panier, qui ferme le bas de l'écran.
   --ann      : hauteur du bandeau d'annonce, qui défile alors que l'en-tête
                reste collant. Sans lui, l'espace entre l'en-tête et le titre
                grandissait de 34 px dès qu'on défilait.
   --prev-top : position d'origine de l'aperçu dans le flux. L'ancrage collant
                vaut cette valeur moins le bandeau, donc l'aperçu descend avec
                la page tant que le bandeau est là, puis se fige à la même
                distance de l'en-tête. */
(function reperes() {
  const hdr = document.getElementById('header');
  const ann = document.querySelector('.announce');
  const prev = document.querySelector('.studio-preview');
  const racine = document.documentElement.style;
  const maj = () => {
    if (hdr) racine.setProperty('--hdr', hdr.offsetHeight + 'px');
    racine.setProperty('--ann', (ann ? ann.offsetHeight : 0) + 'px');
    if (composeBar) racine.setProperty('--bar', composeBar.offsetHeight + 'px');
    if (prev) {
      /* Mesurée l'aperçu décroché : collé, il donne sa position à l'écran, défilement
         compris. Un redimensionnement ou la fin du chargement survenus page défilée
         l'envoyaient alors en bas de l'écran, rapetissé (signalé par Nathan le 21/10/2026). */
      const pos = prev.style.position;
      prev.style.position = 'static';
      let y = 0;
      for (let e = prev; e; e = e.offsetParent) y += e.offsetTop;
      prev.style.position = pos;
      racine.setProperty('--prev-top', y + 'px');
    }
  };
  maj();
  window.addEventListener('resize', maj, { passive: true });
  window.addEventListener('load', maj);
})();

/* Photo composeur déposée dans Shopify, recadrée au ras de la boucle (28/10/2026) : le
   cadre Largeur × Hauteur recevait aussi les marges transparentes du PNG, qui
   rapetissaient la boucle (N20 un tiers trop petite) et obligeaient Charlotte à gonfler
   les cotes (N403 saisie à 160 × 120). Rend { src, ar } (ar = largeur / hauteur de la
   boucle visible), ou null si l'image ne se lit pas : la photo est alors posée telle quelle. */
const bouclesRognees = {};
function rogneBoucle(url) {
  if (!bouclesRognees[url]) bouclesRognees[url] = new Promise(resolve => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => {
      try {
        const W = im.naturalWidth, H = im.naturalHeight;
        const c = document.createElement('canvas'); c.width = W; c.height = H;
        const x = c.getContext('2d'); x.drawImage(im, 0, 0);
        const a = x.getImageData(0, 0, W, H).data;
        let x0 = W, x1 = -1, y0 = H, y1 = -1;
        /* Un pixel presque transparent égaré loin de la boucle (ombre, poussière du détourage)
           agrandissait le cadre : la N20 sortait trop petite et calée en haut (28/10/2026).
           Seuls comptent les pixels opaques au quart au moins. */
        for (let y = 0; y < H; y++) for (let k = 0; k < W; k++)
          if (a[(y * W + k) * 4 + 3] > 64) { if (k < x0) x0 = k; if (k > x1) x1 = k; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        if (x1 < 0) { resolve(null); return; }
        const bw = x1 - x0 + 1, bh = y1 - y0 + 1;
        if (bw === W && bh === H) { resolve({ src: url, ar: W / H }); return; }   /* déjà au ras, ou JPG sans transparence */
        const o = document.createElement('canvas'); o.width = bw; o.height = bh;
        o.getContext('2d').drawImage(c, x0, y0, bw, bh, 0, 0, bw, bh);
        resolve({ src: o.toDataURL('image/png'), ar: bw / bh });
      } catch (e) { resolve(null); }
    };
    im.onerror = () => resolve(null);
    im.src = url;
  });
  return bouclesRognees[url];
}
/* Taille et décalage (en cqw) d'une photo Shopify recadrée. Boucle vendue avec son passant
   (champs Passant remplis) : la photo montre les deux pièces, passant à gauche ; la hauteur
   fait l'échelle, la Largeur n'étant que celle de la boucle, et l'ensemble glisse à droite
   de 17 % de sa largeur pour poser la boucle sur la tige, comme les réglages dx des N312,
   N335, N379 et N380. Sinon la boucle remplit Largeur × Hauteur sans se déformer. Sans
   dimensions, même surface que les autres boucles. */
function tailleShopify(b, ar, mm, cqwPerMm) {
  if (mm && b.passantShopify) {
    const h = Math.max(mm[1], b.passantShopify[0], b.passantShopify[1]) * cqwPerMm;
    return { w: h * ar, h: h, dx: 0.17 * h * ar };
  }
  if (mm) {
    const w = Math.min(mm[0], mm[1] * ar) * cqwPerMm;
    return { w: w, h: w / ar, dx: 0 };
  }
  const a = Math.sqrt(ar), S = buckleSize();
  return { w: S * a, h: S / a, dx: 0 };
}

/* ---------- Aperçu + récap ---------- */
function renderPreview() {
  /* ne réassigner le src QUE s'il a changé → l'animation de swap (load) ne se rejoue
     que sur l'élément modifié (boucle OU tige), pas sur les deux à la fois.
     getAttribute compare la valeur brute (relative), pas l'URL absolue résolue. */
  if (strapImg.dataset.tige !== sel.tige.id + '|' + width) {
    const t = sel.tige;
    strapImg.dataset.tige = t.id + '|' + width;
    if (!t.aCaler) strapImg.src = t.img;
    else caleTige(t.img, width).then(src => { if (sel.tige === t && strapImg.getAttribute('src') !== src) strapImg.src = src; });
  }
  /* une photo Shopify n'est posée qu'une fois recadrée, plus bas */
  if (!sel.boucle.deShopify && buckleImg.getAttribute('src') !== sel.boucle.img) buckleImg.src = sel.boucle.img;
  /* Les réglages fins (BUCKLE_TWEAKS, BOUCLE_FIT) ont été mesurés sur les détourages
     du site : ils ne valent pas pour une photo déposée dans Shopify. */
  const tw = sel.boucle.deShopify ? {} : (BUCKLE_TWEAKS[sel.boucle.id] || {});
  buckleImg.style.left = (((sel.tige.cx != null ? sel.tige.cx : 0.5) + (tw.dx || 0)) * 100).toFixed(1) + '%'; /* boucle à l'emplacement du passant */
  buckleImg.style.top = ((sel.tige.cy + (tw.dy || 0)) * 100).toFixed(1) + '%'; /* cale la boucle sur la sangle */
  /* Taille de la boucle, ancrée sur l'épaisseur RÉELLE de la sangle rendue :
     la bande fait 150px sur un canevas de 1086 (40 mm) et 93,5px (25 mm),
     soit 13,81 et 8,61 cqw. Donc 1 mm ≈ 0,345 cqw, vraie échelle.
     Priorité : 1. dimensions du book (BOUCLE_MM) — fiables même pour les
     boucles bijou dont la tige s'attache derrière ; 2. passage intérieur
     mesuré (BOUCLE_FIT) — l'ouverture reçoit exactement la sangle, ce qui
     est le critère « l'ardillon rentre pile » ; borné, car sur une boucle
     décorative le trou visible n'est pas le passage ; 3. ancien calcul. */
  /* Ancre = la TIGE DE FACE seulement (découverte client 26/08) : la photo
     montre la ceinture bouclée, le dos visible au-dessus de la face gonflait
     l'ancienne ancre (150 px = enveloppe face+dos). La face mesurée par la
     ligne des trous fait 110 px (40 mm) et 62 px (25 mm) sur canevas 1086. */
  const STRAP_CQW = { 40: 10.13, 25: 5.71 };
  const strapCqw = STRAP_CQW[width] || 10.13;
  const cqwPerMm = strapCqw / width;
  /* Source officielle d'abord : dimensions fournies par la maison, par code et
     largeur (MAISON_MM). Ensuite le book, puis le passage mesuré. */
  const base = (typeof boucleBase === 'function') ? (boucleBase(sel.boucle) || '').toUpperCase() : '';
  const officiel = (typeof MAISON_MM !== 'undefined' && base) ? MAISON_MM[base + '|' + width] : null;
  const mm = tw.mm || sel.boucle.mmShopify || officiel || ((typeof BOUCLE_MM !== 'undefined') ? BOUCLE_MM[sel.boucle.id] : null);
  const fitP = (!sel.boucle.deShopify && typeof BOUCLE_FIT !== 'undefined') ? BOUCLE_FIT[sel.boucle.id] : null;
  const scl = tw.scale || 1;
  if (sel.boucle.deShopify) {
    const b = sel.boucle, cx = sel.tige.cx != null ? sel.tige.cx : 0.5;
    rogneBoucle(b.img).then(r => {
      if (sel.boucle !== b) return;
      const src = r ? r.src : b.img;
      if (buckleImg.getAttribute('src') !== src) buckleImg.src = src;
      const t = tailleShopify(b, r ? r.ar : (b.aspect || 1), mm, cqwPerMm);
      buckleImg.style.width = t.w.toFixed(1) + 'cqw';
      buckleImg.style.height = t.h.toFixed(1) + 'cqw';
      buckleImg.style.left = (cx * 100 + t.dx).toFixed(1) + '%';
    });
  } else if (mm) {
    buckleImg.style.width = (mm[0] * cqwPerMm * scl).toFixed(1) + 'cqw';
    buckleImg.style.height = (mm[1] * cqwPerMm * scl).toFixed(1) + 'cqw';
  } else if (fitP && fitP[1] >= 0.40 && fitP[1] <= 0.95) {
    const h = strapCqw / fitP[1];
    buckleImg.style.width = (h * fitP[0] * scl).toFixed(1) + 'cqw';
    buckleImg.style.height = (h * scl).toFixed(1) + 'cqw';
  } else {
    const a = Math.sqrt(sel.boucle.aspect || 1);
    const S = buckleSize() * scl;
    buckleImg.style.width = (S * a).toFixed(1) + 'cqw';
    buckleImg.style.height = (S / a).toFixed(1) + 'cqw';
  }

  /* Les paramètres de campagne (gclid de Google Ads, utm_, fbclid, gtm_debug de l'assistant de balises Google) restent dans l'adresse. */
  const campagne = new URLSearchParams();
  new URLSearchParams(location.search).forEach((v, k) => { if (/^(utm_|gad_|gclid$|gbraid$|wbraid$|fbclid$|srsltid$|msclkid$|gtm_debug$|_gl$)/.test(k)) campagne.set(k, v); });
  history.replaceState(null, '', `?w=${width}&tige=${sel.tige.id}&boucle=${sel.boucle.id}` + (sel.taille ? `&t=${sel.taille}` : '') + (campagne.toString() ? '&' + campagne : ''));

  /* légende sous l'aperçu (téléphone) : cuir, boucle, largeur */
  const cap = document.getElementById('stageCap');
  if (cap) cap.textContent = `${sel.tige.name} · ${String(sel.boucle.name || '').replace(/^BOUCLE\s+/i, 'Boucle ')} · ${width} mm`;
  document.getElementById('sumLeather').textContent = `${sel.tige.name} · ${width} mm` + (sel.taille ? ` · taille ${sel.taille}` : '');
  /* Tige ou boucle épuisée : elle reste dans la composition, mais ne partira
     pas au panier. Le récapitulatif le dit et ne la compte pas. */
  const tOut = tigeOut(sel.tige);
  document.getElementById('sumLeatherPrice').textContent = tOut ? 'Non disponible' : euro(sel.tige.price);
  document.getElementById('sumBuckle').textContent = `${sel.boucle.name} · ${width} mm`;
  /* Boucle épuisée : elle reste visible et composable, mais seule la tige partira
     au panier. Le récapitulatif le dit et ne la compte pas (24/09/2026). */
  const bOut = boucleOut(sel.boucle);
  const totalCompo = (tOut ? 0 : sel.tige.price) + (bOut ? 0 : sel.boucle.price);
  document.getElementById('sumBucklePrice').textContent = bOut ? 'Non disponible' : euro(sel.boucle.price);
  document.getElementById('sumTotal').textContent = euro(totalCompo);
  /* barre fixe (téléphone) : même composition, même prix */
  const bs = document.getElementById('barSummary'), bt = document.getElementById('barTotal');
  if (bs) bs.textContent = `${sel.tige.name} · ${(sel.boucle.name || '').replace(/^BOUCLE\s+/i, 'Boucle ')}` + (sel.taille ? ` · T${sel.taille}` : '');
  if (bt) bt.textContent = euro(totalCompo);
}

/* ---------- Largeur (25 / 40 mm) : filtre tiges + boucles ---------- */
const widthChoice = document.getElementById('widthChoice');
function renderWidth() {
  if (!widthChoice) return;
  widthChoice.querySelectorAll('.wf-btn').forEach(b =>
    b.classList.toggle('active', +b.dataset.w === width));
}
widthChoice?.addEventListener('click', e => {
  const b = e.target.closest('.wf-btn'); if (!b) return;
  const w = b.dataset.w === '25' ? 25 : 40;
  if (w === width) return;
  width = w;
  TIGES = buildTiges(width);
  BOUCLES = buildBoucles(width);
  if (!BOUCLES.length) { width = 40; TIGES = buildTiges(40); BOUCLES = buildBoucles(40); } // garde-fou
  if (!TIGES.find(t => t.id === sel.tige.id)) sel.tige = tigeDefaut();
  if (!BOUCLES.find(x => (x.uid || x.id) === (sel.boucle.uid || sel.boucle.id))) sel.boucle = boucleDefaut();
  boucleStyle = 'all';
  renderWidth(); renderStyles(); renderTiges(); renderBoucles(); renderPreview();
});

/* ---------- Sélection ---------- */
tigeRail.addEventListener('click', e => {
  const b = e.target.closest('[data-tige]'); if (!b) return;
  sel.tige = TIGES.find(t => t.id === b.dataset.tige);
  renderTiges(); renderPreview();
});

boucleStyles.addEventListener('click', e => {
  const b = e.target.closest('[data-bstyle]'); if (!b) return;
  boucleStyle = b.dataset.bstyle;
  renderStyles(); renderBoucles();
});

boucleRail.addEventListener('click', e => {
  const b = e.target.closest('[data-boucle]'); if (!b || b.classList.contains('swatch-out')) return;
  sel.boucle = BOUCLES.find(x => (x.uid || x.id) === b.dataset.boucle) || BOUCLES.find(x => x.id === b.dataset.boucle);
  renderBoucles(); renderPreview();
});

/* ---------- Ajout au panier (ceinture composée) ---------- */
/* La tige et la boucle entrent séparément dans le panier. La tige attend sa taille,
   chaque tige composée reste une ligne à part (deux tiges peuvent avoir deux tailles). */
document.getElementById('addCustom').addEventListener('click', () => {
  /* Tige et boucle épuisées : rien ne part, on le dit. */
  const tigeEpuisee = tigeOut(sel.tige);
  if (tigeEpuisee && boucleOut(sel.boucle)) { toast('Tige et boucle en rupture de stock, rien n’a été ajouté au panier'); return; }
  /* Sans taille, on n'ajoute rien : on amène la cliente aux tailles et on le dit.
     Une tige épuisée ne part pas, sa taille n'est donc pas demandée. */
  if (!sel.taille && !tigeEpuisee) {
    toast('Choisissez votre taille');
    if (tailleBlock) tailleBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (tailleChips) { tailleChips.classList.remove('pd-nudge'); void tailleChips.offsetWidth; tailleChips.classList.add('pd-nudge'); }
    return;
  }
  /* Règle de Nathan (24/09/2026) : une boucle épuisée se compose, mais à l'ajout le
     site dit « Boucle non disponible » et ne met que la tige au panier. */
  const boucleEpuisee = boucleOut(sel.boucle);
  const lignes = [{
    id: sel.tige.id, type: 'tige',
    name: `Tige ${sel.tige.name}`, vlabel: `${width} mm · T${sel.taille}`,
    price: sel.tige.price, img: sel.tige.img,
    key: `tige|${sel.tige.id}|${Date.now()}`, size: sel.taille,
  }, {
    id: sel.boucle.id, type: 'boucle',
    name: /^boucle/i.test(sel.boucle.name) ? sel.boucle.name : `Boucle ${sel.boucle.name}`, vlabel: `${width} mm`,
    price: sel.boucle.price, img: sel.boucle.img, color: sel.boucle.colorLabel || '',
    voption: sel.boucle.voption || '',
    key: `boucle|${sel.boucle.id}|${width}`,
  }];
  if (boucleEpuisee) lignes.pop();
  /* Tige épuisée (6 oct 2026) : même règle, seule la boucle part au panier. */
  if (tigeEpuisee) lignes.shift();
  /* Sur le thème, les deux lignes partent en une seule requête et s'affichent
     tout de suite (cartAddLines, cart-shopify.js) ; false = rien d'ajouté, on
     ne dit pas le contraire. Sur la copie statique, le panier local les prend
     une par une. */
  if (typeof cartAddLines === 'function') { if (cartAddLines(lignes) === false) return; }
  else lignes.forEach(line => cartAdd(line));
  renderCart();
  toast(tigeEpuisee ? 'Tige en rupture de stock. Boucle ajoutée au panier'
      : boucleEpuisee ? `Boucle non disponible. Tige ajoutée au panier, taille ${sel.taille}` : `Ceinture ajoutée au panier, taille ${sel.taille}`);
  openDrawer();
});

/* ---------- Taille : pastilles, guide, barre fixe ---------- */
tailleChips?.addEventListener('click', e => {
  const b = e.target.closest('.pd-chip'); if (!b) return;
  sel.taille = b.dataset.size;
  renderTaille(); renderPreview();
});
document.getElementById('addCustomBar')?.addEventListener('click', () => document.getElementById('addCustom').click());
(function guideTailles() {
  const btn = document.getElementById('sizeGuideBtn'), ov = document.getElementById('sizeOverlay'), rows = document.getElementById('sizeRows');
  if (!btn || !ov) return;
  const G = (typeof TAILLES_GUIDE !== 'undefined') ? TAILLES_GUIDE : [];
  if (rows) rows.innerHTML = G.map(r => `<tr><td>${r[0]}</td><td>${r[1]}</td><td>${r[2]}</td></tr>`).join('');
  btn.addEventListener('click', () => ov.classList.add('open'));
  document.getElementById('sizeClose')?.addEventListener('click', () => ov.classList.remove('open'));
  ov.addEventListener('click', e => { if (e.target === ov) ov.classList.remove('open'); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') ov.classList.remove('open'); });
})();
/* La barre fixe n'apparaît que quand le récapitulatif n'est pas à l'écran
   (téléphone, pendant la composition). Le CSS la retire sur grand écran. */
(function barreFixe() {
  /* Toujours visible, sur tous les écrans (demande de Nathan, 20 sept 2026) :
     le prix et le bouton d'ajout ne quittent jamais le bas de l'écran. */
  if (!composeBar) return;
  composeBar.classList.add('is-visible');
  composeBar.setAttribute('aria-hidden', 'false');
  document.body.classList.add('compose-bar-on');
})();

/* ---------- Init (largeur depuis l'URL) ---------- */
/* Largeur déduite de la boucle demandée quand l'URL ne la précise pas (lien depuis une fiche 25 mm). */
const boucleDemandee = q.get('boucle') && (typeof PRODUCTS !== 'undefined') ? PRODUCTS.find(p => p.id === q.get('boucle')) : null;
if (q.get('w') === '25' || (!q.get('w') && boucleDemandee && boucleDemandee.width === 25)) {
  width = 25;
  TIGES = buildTiges(25);
  BOUCLES = buildBoucles(25);
  if (!BOUCLES.length) { width = 40; TIGES = buildTiges(40); BOUCLES = buildBoucles(40); }
  sel.tige = TIGES.find(t => t.id === tigeDemandee) || tigeDefaut();
  sel.boucle = BOUCLES.find(b => (b.uid || b.id) === q.get('boucle')) || BOUCLES.find(b => b.id === q.get('boucle')) || boucleDefaut();
}
/* Plus de saut vers un coloris en stock au chargement : la composition
   d'ouverture est Preto noire et le premier best-seller en stock (28 oct 2026). */
renderWidth();
renderTiges();
renderStyles();
renderBoucles();
renderTaille();
renderPreview();

/* Une fiche envoie sa pièce dans l'adresse. Si elle n'est pas composable (pas de
   découpe, hors sélection), on le dit au lieu de montrer une autre pièce en silence. */
(function verifierDemande() {
  const bd = q.get('boucle');
  const msgs = [];
  if (bd && (sel.boucle.uid || sel.boucle.id) !== bd && sel.boucle.id !== bd) msgs.push("Cette boucle n'est pas encore dans le composeur, en voici une autre.");
  if (tigeDemandee && sel.tige.id !== tigeDemandee) msgs.push("Ce cuir n'est pas dans le composeur, en voici un autre.");
  if (msgs.length && typeof toast === 'function') setTimeout(() => toast(msgs.join(' ')), 700);
})();
